import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { AnimatedSection } from "@/components/AnimatedSection";
import { AnimatedCard } from "@/components/AnimatedCard";

const SoftwareRecursos = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-blue-600 relative overflow-x-hidden">
      <Header />
      
      <div className="pt-20 sm:pt-24">
        {/* Hero Section with Gradient */}
        <section className="relative w-full">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950"></div>
          <article className="relative z-10 flex flex-col justify-center items-center text-center xl:max-w-5xl gap-4 text-xl text-white mx-auto py-20 px-4 lg:py-32 lg:px-16 xl:px-28">
            <h1 className="text-3xl sm:text-4xl font-bold">Software e Recursos</h1>
            <p className="max-w-3xl">
              Transforme a gestão dos seus recursos hídricos com nossa plataforma que utiliza 
              inteligência artificial e tecnologia de ponta para oferecer consistência de dados 
              hidrológicos, modelagem de curva chave e gestão completa de ativos.
            </p>
            <a
              href="https://ahq-engenharia.lovable.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 hover:border-blue-400 hover:scale-105 py-3 px-10 font-semibold text-white rounded-xl text-sm text-center uppercase transition-all duration-200 border border-blue-700"
            >
              <ExternalLink className="w-4 h-4" />
              <strong>Acessar Plataforma</strong>
            </a>
          </article>
        </section>

        {/* Content Sections */}
        <section className="py-10 sm:py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            
            {/* O Problema que Vamos Resolver */}
            <div className="mb-16 overflow-hidden">
              <AnimatedSection className="px-2 sm:px-0" delay={100}>
                <h2 className="text-3xl font-bold text-blue-600 mb-4">O Problema que Vamos Resolver</h2>
                <div className="w-[200px] h-[2px] bg-gray-300 mb-8"></div>
              </AnimatedSection>
              
              <div className="space-y-6">
                <AnimatedCard alignment="left" delay={150}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-4">Desafios do Setor</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <span className="mr-2 text-blue-600">•</span>
                        <span>Consistência de dados hidrológicos por plataforma SaaS com uso de IA</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-blue-600">•</span>
                        <span>Levantamento de dados para usinas hidrelétricas, saneamento e construção</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-blue-600">•</span>
                        <span>Modelagem de curva chave</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-blue-600">•</span>
                        <span>Gestão de dados para empreendimentos</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-blue-600">•</span>
                        <span>Gestão de ativos</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-blue-600">•</span>
                        <span>Garantia física</span>
                      </li>
                    </ul>
                  </div>
                </AnimatedCard>
                
                <AnimatedSection className="flex justify-end" delay={250}>
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl w-full max-w-[600px]">
                    <img 
                      src="/lovable-uploads/42cd7ecf-459c-457c-af0c-c7a0087afe74.png" 
                      alt="Barragem e recursos hídricos" 
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </AnimatedSection>
              </div>
            </div>

            {/* Proposta de Valor */}
            <div className="mb-16 overflow-hidden">
              <AnimatedSection className="px-2 sm:px-0" delay={100}>
                <h2 className="text-3xl font-bold text-blue-600 mb-4">Proposta de Valor</h2>
                <div className="w-[200px] h-[2px] bg-gray-300 mb-8"></div>
                <p className="text-gray-700 text-lg mb-6 max-w-3xl">
                  Nossa proposta de valor é construída sobre quatro pilares fundamentais que garantem 
                  excelência na gestão de recursos hídricos.
                </p>
              </AnimatedSection>
              
              <AnimatedSection className="flex justify-end mb-8" delay={150}>
                <img 
                  src="/lovable-uploads/2f5e278e-f6a6-4a6e-a81d-2728ba777d74.png" 
                  alt="Proposta de Valor" 
                  className="rounded-lg shadow-lg w-full max-w-[600px]"
                />
              </AnimatedSection>

              <div className="space-y-6">
                <AnimatedCard alignment="left" delay={200}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Consistência de Dados</h3>
                    <p className="text-gray-700">
                      Serviços especializados para garantir dados hidrológicos precisos e validados, 
                      essenciais para tomada de decisões estratégicas.
                    </p>
                  </div>
                </AnimatedCard>
                
                <AnimatedCard alignment="right" delay={300}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Informações Confiáveis</h3>
                    <p className="text-gray-700">
                      Base sólida de informações sobre recursos hídricos para tomada de decisões 
                      estratégicas com segurança e precisão.
                    </p>
                  </div>
                </AnimatedCard>
                
                <AnimatedCard alignment="left" delay={400}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Redução de Custos</h3>
                    <p className="text-gray-700">
                      Otimização operacional que gera economia significativa para empresas do setor 
                      elétrico e de saneamento.
                    </p>
                  </div>
                </AnimatedCard>
                
                <AnimatedCard alignment="right" delay={500}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Oportunidades de Negócio</h3>
                    <p className="text-gray-700">
                      Identificação de potencial e novas oportunidades na área de recursos hídricos 
                      para expansão sustentável.
                    </p>
                  </div>
                </AnimatedCard>
              </div>
              
              <div className="text-center mt-8">
                <a
                  href="https://ahq-engenharia.lovable.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 hover:border-blue-400 hover:scale-105 py-3 px-8 font-semibold text-white rounded-xl text-sm uppercase transition-all duration-200 border border-blue-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  Acessar Plataforma
                </a>
              </div>
            </div>

            {/* Arquitetura do Sistema */}
            <div className="mb-16 overflow-hidden">
              <AnimatedSection className="px-2 sm:px-0" delay={100}>
                <h2 className="text-3xl font-bold text-blue-600 mb-4">Arquitetura do Sistema</h2>
                <div className="w-[200px] h-[2px] bg-gray-300 mb-8"></div>
                <p className="text-gray-700 text-lg mb-6 max-w-3xl">
                  Nossa arquitetura foi projetada para garantir escalabilidade, segurança e performance 
                  na gestão dos seus dados hidrológicos.
                </p>
              </AnimatedSection>
              
              <AnimatedSection className="flex justify-end mb-8" delay={150}>
                <img 
                  src="/lovable-uploads/ae164650-a423-4cde-8737-8dcb423ded2a.png" 
                  alt="Arquitetura do Sistema" 
                  className="rounded-lg shadow-lg w-full max-w-[600px]"
                />
              </AnimatedSection>

              <div className="space-y-6">
                <AnimatedCard alignment="left" delay={200}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Infraestrutura em Nuvem</h3>
                    <p className="text-gray-700">
                      Escalabilidade e disponibilidade garantidas com infraestrutura cloud de última 
                      geração, permitindo acesso seguro de qualquer lugar.
                    </p>
                  </div>
                </AnimatedCard>
                
                <AnimatedCard alignment="right" delay={300}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Conexão Segura</h3>
                    <p className="text-gray-700">
                      Comunicação bidirecional segura entre usuários e servidor AHQ com criptografia 
                      de ponta para proteção total dos dados.
                    </p>
                  </div>
                </AnimatedCard>
                
                <AnimatedCard alignment="left" delay={400}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Modelos Preditivos</h3>
                    <p className="text-gray-700">
                      Análise avançada com inteligência artificial para previsões precisas e confiáveis, 
                      antecipando cenários e otimizando operações.
                    </p>
                  </div>
                </AnimatedCard>
                
                <AnimatedCard alignment="right" delay={500}>
                  <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-2xl">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">Integração de Dados</h3>
                    <p className="text-gray-700">
                      Processamento inteligente dos dados de estação fornecidos pelo cliente, 
                      garantindo consistência e qualidade das informações.
                    </p>
                  </div>
                </AnimatedCard>
              </div>
              
              <div className="text-center mt-12">
                <a
                  href="https://ahq-engenharia.lovable.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 hover:border-blue-400 hover:scale-105 py-3 px-8 font-semibold text-white rounded-xl text-sm uppercase transition-all duration-200 border border-blue-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  Acessar Plataforma
                </a>
              </div>
            </div>

          </div>
        </section>
      </div>
    </div>
  );
};

export default SoftwareRecursos;
